import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NumberField, TextField } from '../../components/Form';
import { LocalDataTable } from '../../components/LocalDataTable';
import { Badge, type BadgeTone } from '../../components/PageHeader';
import { api } from '../../lib/api';
import { money, monthYear, useMutation, useResource } from '../../lib/hooks';

/**
 * Pakistan Standard Time has no DST, so it is always UTC+5 - a fixed shift regardless of
 * the viewer's own timezone. Mirrors the server's ConvertToMyTimeZone(), which the legacy
 * statement and its Excel export both classify submissions by.
 */
const PKT_OFFSET_MS = 5 * 60 * 60 * 1000;

function toPakistanTime(iso: string): Date {
  return new Date(new Date(iso).getTime() + PKT_OFFSET_MS);
}

function formatPakistanDate(iso: string): string {
  return toPakistanTime(iso).toLocaleDateString(undefined, { timeZone: 'UTC' });
}

type TeacherSalaryView = {
  teacherMonthSalary: {
    id: number; month: number; year: number; sharePercent: number;
    paidAmount: number; isOpen: boolean; teacher?: { id: number; name: string };
  };
  totalFeesRequiredForThisMonth: number;
  totalFeesSubmittedForThisMonth: number;
  teacherSharePercent: number;
  feesSubmittedForPreviousMonthInThisMonth: number;
  payableAmountForThisMonth: number;
  payableAmountForPreviousMonthInThisMonth: number;
  payableAmount: number;
  paidAmount: number;
  remainingAmount: number;
};

/** Two-column figure row, used on both salary screens. */
function Figure({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '7px 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="money" style={{ fontWeight: strong ? 700 : 400 }}>{value}</span>
    </div>
  );
}

/**
 * Teacher salary payment.
 *
 * The payable figure is recomputed server-side before the guard runs, so unlike student fee
 * submission this screen cannot be tricked into overpaying by tampering with the request.
 */
export function TeacherSalaryPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const view = useResource<TeacherSalaryView>(id ? `/api/teacher-salary/${id}/payment` : null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  async function pay() {
    const ok = await run(
      () => api.post('/api/teacher-salary/payment', {
        teacherMonthSalaryId: Number(id),
        requiredAmount: Number(amount) || 0,
        note,
      }),
      'Salary payment recorded.',
      () => view.reload(),
    );

    if (ok) {
      setAmount('');
      setNote('');
    }
  }

  const salary = view.data?.teacherMonthSalary;

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">
            Teacher salary payment{salary?.teacher ? ` — ${salary.teacher.name}` : ''}
          </h2>
          <p className="page__subtitle">
            {salary ? `${monthYear(salary.month, salary.year)} · ${salary.sharePercent}% share` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {id && (
            <button type="button" className="btn" onClick={() => navigate(`/salary/teachers/${id}/statement`)}>
              Statement
            </button>
          )}
          <button type="button" className="btn" onClick={() => navigate('/salary/teachers')}>Back</button>
        </div>
      </div>

      {view.loading && <div className="card"><p>Loading…</p></div>}
      {view.error && <div className="card"><p className="field__error">{view.error}</p></div>}

      {view.data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
          <div className="card">
            <h3 className="card__title">This month</h3>
            <Figure label="Fees required" value={money(view.data.totalFeesRequiredForThisMonth)} />
            <Figure label="Fees submitted" value={money(view.data.totalFeesSubmittedForThisMonth)} />
            <Figure label="Payable" value={money(view.data.payableAmountForThisMonth)} />

            <h3 className="card__title" style={{ marginTop: 20 }}>Collected for earlier months</h3>
            <Figure label="Fees submitted" value={money(view.data.feesSubmittedForPreviousMonthInThisMonth)} />
            <Figure label="Payable" value={money(view.data.payableAmountForPreviousMonthInThisMonth)} />

            <h3 className="card__title" style={{ marginTop: 20 }}>Total</h3>
            <Figure label="Total payable" value={money(view.data.payableAmount)} strong />
            <Figure label="Already paid" value={money(view.data.paidAmount)} />
            <Figure label="Remaining" value={money(view.data.remainingAmount)} strong />
          </div>

          <div className="card">
            <h3 className="card__title">Record a payment</h3>
            <NumberField
              id="amount" label="Amount" value={amount} onChange={setAmount}
              hint={`Cannot exceed the remaining ${money(view.data.remainingAmount)}.`}
            />
            <TextField id="note" label="Note" value={note} onChange={setNote} />

            <button
              type="button"
              className="btn btn--accent"
              disabled={busy || !(Number(amount) > 0)}
              onClick={() => void pay()}
            >
              {busy ? 'Saving…' : 'Record payment'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

type Statement = TeacherSalaryView & {
  feesRequiredForThisMonth: number;
  feesSubmittedAfterMonth: number;
  feesPaymentsForThisMonth: StatementRow[];
  feesPaymentsForPreviousMonth: StatementRow[];
};

type StatementRow = {
  feesId: number;
  amount: number;
  createdDateTime: string;
  feesSlipId: number;
  fees?: {
    month: number | null; year: number | null; amount: number; paidAmount: number;
    studentCourse?: {
      student?: { id: number; name: string };
      course?: { grade?: { description: string }; subject?: { description: string }; groupName?: string };
    };
  };
};

/**
 * The teacher's monthly statement — the document they're actually shown.
 *
 * The four buckets (in month / after month / for a previous month / not submitted) are
 * computed server-side, including an inconsistency between UTC and Pakistan-time
 * comparisons that is faithfully carried over from the original.
 */
export function TeacherStatementPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const statement = useResource<Statement>(id ? `/api/teacher-salary/${id}/statement` : null);

  async function download() {
    await run(async () => {
      const response = await fetch(`/api/teacher-salary/${id}/statement/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ccms.token')}` },
      });
      if (!response.ok) throw new Error('Could not build the statement.');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `teacher-statement-${id}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'Statement downloaded.');
  }

  // The two buckets are shown in one paged table. Bucket alone is not shown - the Status
  // column below is a strict refinement of it (a previous-month row is always "Submitted
  // for previous month"; a this-month row is further split three ways), exactly what the
  // legacy statement and its Excel export show per row.
  const statementRows: { row: StatementRow; bucket: 'This month' | 'Previous month' }[] = [
    ...(statement.data?.feesPaymentsForThisMonth ?? []).map((row) => ({ row, bucket: 'This month' as const })),
    ...(statement.data?.feesPaymentsForPreviousMonth ?? []).map((row) => ({ row, bucket: 'Previous month' as const })),
  ];

  function submissionStatus(entry: { row: StatementRow; bucket: 'This month' | 'Previous month' }):
    { text: string; tone: BadgeTone } {
    if (entry.bucket === 'Previous month') return { text: 'Submitted for previous month', tone: 'info' };

    if (!entry.row.fees || entry.row.fees.paidAmount === 0) return { text: 'Not submitted', tone: 'danger' };

    const salary = statement.data!.teacherMonthSalary;
    const created = toPakistanTime(entry.row.createdDateTime);
    const inMonth = (created.getUTCFullYear() === salary.year && created.getUTCMonth() + 1 <= salary.month)
      || created.getUTCFullYear() < salary.year;

    return inMonth
      ? { text: 'Submitted in month', tone: 'success' }
      : { text: 'Submitted after month', tone: 'warning' };
  }

  const salary = statement.data?.teacherMonthSalary;

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Monthly statement</h2>
          <p className="page__subtitle">
            {statement.data
              ? `${salary?.teacher?.name} · ${monthYear(salary?.month, salary?.year)}`
              : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn--primary" onClick={() => void download()} disabled={busy}>
            {busy ? 'Working…' : 'Export to Excel'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/salary/teachers')}>Back</button>
        </div>
      </div>

      {statement.loading && <div className="card"><p>Loading…</p></div>}
      {statement.error && <div className="card"><p className="field__error">{statement.error}</p></div>}

      {statement.data && salary && (
        <>
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 var(--sp-5)' }}>
              <Figure label="Teacher ID" value={`T-${salary.teacher?.id}`} />
              <Figure label="Teacher name" value={salary.teacher?.name ?? '—'} />
              <Figure label="Period" value={monthYear(salary.month, salary.year)} />
              <Figure label="Share percent" value={`${salary.sharePercent}%`} />
              <Figure
                label="Status"
                value={<Badge tone={salary.isOpen ? 'success' : 'neutral'}>{salary.isOpen ? 'Open' : 'Closed'}</Badge>}
              />
              <Figure label="Fees required this month" value={money(statement.data.totalFeesRequiredForThisMonth)} />
              <Figure label="Submitted in month" value={money(statement.data.totalFeesSubmittedForThisMonth)} />
              <Figure label="Submitted for earlier months" value={money(statement.data.feesSubmittedForPreviousMonthInThisMonth)} />
              <Figure label="Submitted after month" value={money(statement.data.feesSubmittedAfterMonth)} />
              <Figure label="Still outstanding" value={money(statement.data.feesRequiredForThisMonth)} />
              <Figure label="Total payable" value={money(statement.data.payableAmount)} strong />
              <Figure label="Paid" value={money(statement.data.paidAmount)} />
              <Figure label="Remaining" value={money(statement.data.remainingAmount)} strong />
            </div>
          </div>

          <LocalDataTable
            rows={statementRows}
            rowKey={(r) => `${r.bucket}-${r.row.feesId}-${r.row.createdDateTime}`}
            emptyMessage="No payments in this month."
            columns={[
              {
                key: 'studentId', header: 'Student ID',
                render: (r) => (r.row.fees?.studentCourse?.student
                  ? `STD-${r.row.fees.studentCourse.student.id}` : '—'),
              },
              { key: 'student', header: 'Student', render: (r) => r.row.fees?.studentCourse?.student?.name ?? '—' },
              {
                key: 'course', header: 'Course',
                render: (r) => [r.row.fees?.studentCourse?.course?.grade?.description,
                  r.row.fees?.studentCourse?.course?.subject?.description,
                  r.row.fees?.studentCourse?.course?.groupName].filter(Boolean).join(' · '),
              },
              { key: 'period', header: 'Period', render: (r) => monthYear(r.row.fees?.month, r.row.fees?.year) },
              {
                key: 'fee', header: 'Fee', align: 'right',
                render: (r) => <span className="money">{money(r.row.fees?.amount)}</span>,
              },
              {
                key: 'paid', header: 'Paid', align: 'right',
                render: (r) => <span className="money">{money(r.row.amount)}</span>,
              },
              {
                key: 'status', header: 'Status',
                render: (r) => {
                  const status = submissionStatus(r);
                  return <Badge tone={status.tone}>{status.text}</Badge>;
                },
              },
              {
                key: 'submitted', header: 'Date of submission',
                render: (r) => (r.row.feesSlipId !== 0 ? formatPakistanDate(r.row.createdDateTime) : '—'),
              },
            ]}
          />
        </>
      )}
    </>
  );
}

type TeacherMonthlyPayments = {
  id: number;
  month: number;
  year: number;
  sharePercent: number;
  paidAmount: number;
  isOpen: boolean;
  teacher?: { id: number; name: string };
  teacherPayments?: { id: number; note: string; amount: number; createdDateTime: string }[];
};

/**
 * The ledger of individual payments recorded against one teacher's monthly salary.
 * Ported from TeacherSalaryController.MonthlyPayments - distinct from the monthly
 * statement (fees the teacher's courses collected) and the Pay screen (records a new one).
 */
export function TeacherMonthlyPaymentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const salary = useResource<TeacherMonthlyPayments>(id ? `/api/teacher-salary/${id}/payments` : null);
  const payments = salary.data?.teacherPayments ?? [];

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Monthly payments</h2>
          <p className="page__subtitle">
            {salary.data
              ? `${salary.data.teacher?.name} · ${monthYear(salary.data.month, salary.data.year)}`
              : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {id && (
            <button type="button" className="btn" onClick={() => navigate(`/salary/teachers/${id}/statement`)}>
              Statement
            </button>
          )}
          <button type="button" className="btn" onClick={() => navigate('/salary/teachers')}>Back</button>
        </div>
      </div>

      {salary.loading && <div className="card"><p>Loading…</p></div>}
      {salary.error && <div className="card"><p className="field__error">{salary.error}</p></div>}

      {salary.data && (
        <>
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 var(--sp-5)' }}>
              <Figure label="Teacher ID" value={`T-${salary.data.teacher?.id}`} />
              <Figure label="Teacher name" value={salary.data.teacher?.name ?? '—'} />
              <Figure label="Period" value={monthYear(salary.data.month, salary.data.year)} />
              <Figure label="Share percent" value={`${salary.data.sharePercent}%`} />
              <Figure
                label="Status"
                value={(
                  <Badge tone={salary.data.isOpen ? 'success' : 'neutral'}>
                    {salary.data.isOpen ? 'Open' : 'Closed'}
                  </Badge>
                )}
              />
              <Figure label="Total paid" value={money(salary.data.paidAmount)} strong />
            </div>
          </div>

          <LocalDataTable
            rows={payments}
            rowKey={(r) => r.id}
            emptyMessage="No payments recorded."
            columns={[
              { key: 'date', header: 'Date', render: (r) => new Date(r.createdDateTime).toLocaleDateString() },
              { key: 'note', header: 'Note', render: (r) => r.note || '—' },
              {
                key: 'amount', header: 'Amount', align: 'right',
                render: (r) => <span className="money">{money(r.amount)}</span>,
              },
            ]}
          />
        </>
      )}
    </>
  );
}

type StaffSalaryView = {
  id: number; month: number; year: number;
  totalAmount: number; paidAmount: number; remainingAmount: number; isOpen: boolean;
  staff?: { id: number; name: string };
};

export function StaffSalaryPaymentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const view = useResource<StaffSalaryView>(id ? `/api/staff-salary/${id}/payment` : null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  async function pay() {
    const ok = await run(
      () => api.post('/api/staff-salary/payment', {
        monthSalaryId: Number(id),
        amount: Number(amount) || 0,
        note,
      }),
      'Salary payment recorded.',
      () => view.reload(),
    );

    if (ok) {
      setAmount('');
      setNote('');
    }
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">
            Staff salary payment{view.data?.staff ? ` — ${view.data.staff.name}` : ''}
          </h2>
          <p className="page__subtitle">
            {view.data ? monthYear(view.data.month, view.data.year) : ''}
          </p>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/salary/staff')}>Back</button>
      </div>

      {view.loading && <div className="card"><p>Loading…</p></div>}
      {view.error && <div className="card"><p className="field__error">{view.error}</p></div>}

      {view.data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          <div className="card">
            <h3 className="card__title">Salary</h3>
            <Figure label="Total" value={money(view.data.totalAmount)} strong />
            <Figure label="Paid" value={money(view.data.paidAmount)} />
            <Figure label="Remaining" value={money(view.data.totalAmount - view.data.paidAmount)} strong />
          </div>

          <div className="card">
            <h3 className="card__title">Record a payment</h3>
            <NumberField
              id="amount" label="Amount" value={amount} onChange={setAmount}
              hint="Paying the full remaining amount closes the month."
            />
            <TextField id="note" label="Note" value={note} onChange={setNote} />

            <button
              type="button"
              className="btn btn--accent"
              disabled={busy || !(Number(amount) > 0)}
              onClick={() => void pay()}
            >
              {busy ? 'Saving…' : 'Record payment'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
